/**
 * main.js — loaded on every page
 *
 * Progressive enhancement only, with one exception: the nav dropdowns open on
 * hover/focus-within via CSS, the FAQ and mobile sub-menus are native
 * <details>, and the mobile drawer is only exposed once this script has
 * upgraded its trigger. The mobile product/review carousels (Embla) fall
 * back to a native scroll-snap row without JS, so every card is still
 * reachable — just without the bullets.
 *
 * Enqueued in PHP with:
 *   whytheme_enqueue_script('main');
 */

import EmblaCarousel from 'embla-carousel';
import { initAnimations } from './animate.js';

const OPEN_ATTR = 'data-open';

/* -------------------------------------------------------------------------- */
/* Header dropdowns                                                            */
/* -------------------------------------------------------------------------- */

function initDropdowns() {
    const toggles = document.querySelectorAll('[data-samba-dropdown-toggle]');

    if (!toggles.length) return;

    const closeAll = (except) => {
        toggles.forEach((toggle) => {
            if (toggle === except) return;

            const panel = document.getElementById(toggle.getAttribute('aria-controls'));
            toggle.setAttribute('aria-expanded', 'false');
            panel?.removeAttribute(OPEN_ATTR);
        });
    };

    toggles.forEach((toggle) => {
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));

        if (!panel) return;

        toggle.addEventListener('click', (event) => {
            event.preventDefault();

            const isOpen = toggle.getAttribute('aria-expanded') === 'true';
            closeAll(toggle);

            toggle.setAttribute('aria-expanded', String(!isOpen));

            if (isOpen) {
                panel.removeAttribute(OPEN_ATTR);
            } else {
                panel.setAttribute(OPEN_ATTR, 'true');
            }
        });
    });

    document.addEventListener('click', (event) => {
        if (!event.target.closest('[data-samba-header]')) closeAll();
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeAll();
    });
}

/* -------------------------------------------------------------------------- */
/* Mega menus — category tabs + sub-lists (layout/header-mega.php)             */
/* -------------------------------------------------------------------------- */

function initMegaMenus() {
    document.querySelectorAll('[data-samba-mega="tabs"]').forEach((panel) => {
        // Show the element(s) whose `attribute` equals `value`, hide its siblings.
        const show = (attribute, value) =>
            panel.querySelectorAll(`[${attribute}]`).forEach((el) => {
                el.hidden = el.getAttribute(attribute) !== value;
            });

        // Level 1: hovering or focusing a category shows its pane and picture.
        panel.querySelectorAll('[data-samba-mega-tab]').forEach((tab) => {
            const activate = () => {
                const index = tab.dataset.sambaMegaTab;
                panel.querySelectorAll('[data-samba-mega-tab]').forEach((t) => t.toggleAttribute('data-active', t === tab));
                show('data-samba-mega-pane', index);
                show('data-samba-mega-image', index);
            };
            tab.addEventListener('mouseenter', activate);
            tab.addEventListener('focus', activate);
        });

        // Level 2: a subcategory with children opens its list in column 3
        // (hiding the category's promo card); leaves don't change anything.
        panel.querySelectorAll('[data-samba-mega-pane]').forEach((pane) => {
            const subs = pane.querySelectorAll('[data-samba-mega-sub]');
            subs.forEach((sub) => {
                const activate = () => {
                    subs.forEach((s) => s.toggleAttribute('data-active', s === sub));
                    pane.querySelectorAll('[data-samba-mega-subpane]').forEach((list) => {
                        list.hidden = list.dataset.sambaMegaSubpane !== sub.dataset.sambaMegaSub;
                    });
                    const promo = pane.querySelector('[data-samba-mega-promo]');
                    if (promo) promo.hidden = true;
                };
                sub.addEventListener('mouseenter', activate);
                sub.addEventListener('focus', activate);
            });
        });
    });
}

/* -------------------------------------------------------------------------- */
/* Header collapse — announcement bar slides away on scroll down, back on up   */
/* -------------------------------------------------------------------------- */

function initHeaderCollapse() {
    const header = document.querySelector('[data-samba-header-collapse]');

    if (!header) return;

    // Clamped to the scrollable range, so iOS rubber-banding at either end
    // doesn't read as a direction change.
    const position = () =>
        Math.min(Math.max(window.scrollY, 0), document.documentElement.scrollHeight - window.innerHeight);

    let last = position();
    let height = document.documentElement.scrollHeight;
    let queued = false;

    // A stack (initStickyStacks) whose pinned area spans the header's bottom
    // edge. Stacks that aren't switched on — or are inside a skipped
    // content-visibility section, which measures as an empty box — never count.
    const stacks = [...document.querySelectorAll('[data-samba-stack]')];
    const stackPinned = () => {
        const edge = header.getBoundingClientRect().bottom;
        return stacks.some((stack) => {
            if (!stack.hasAttribute('data-stack-on')) return false;
            const body = (stack.querySelector('[data-samba-stack-body]') || stack).getBoundingClientRect();
            return body.top <= edge + 1 && body.bottom > edge;
        });
    };

    const update = () => {
        queued = false;
        const y = position();

        // The page changing height (a deferred section rendering at its real
        // size, images loading) can make the browser nudge scrollY up to keep
        // the view steady. That isn't the visitor scrolling up, so it mustn't
        // bring the bar back; a real scroll down in the same frame still counts.
        const newHeight = document.documentElement.scrollHeight;
        const resized = newHeight !== height;
        height = newHeight;
        if (resized && y < last) {
            last = y;
            return;
        }

        // Sub-pixel noise (zoom, fractional scroll) isn't a direction.
        if (Math.abs(y - last) < 1) return;

        // Stacking cards (events) pin just under the header — while they're
        // pinned, sliding the bar would drag them 36/50px with it, so the
        // header holds whatever state it's in until the stack scrolls away.
        if (stackPinned()) {
            last = y;
            return;
        }

        // The transition (header.php) reverses from its current point when
        // this flips mid-slide.
        header.toggleAttribute('data-collapsed', y > last && y > 0);
        last = y;
    };

    if (last > 0) header.setAttribute('data-collapsed', '');

    window.addEventListener(
        'scroll',
        () => {
            if (!queued) {
                queued = true;
                requestAnimationFrame(update);
            }
        },
        { passive: true }
    );
}

/* -------------------------------------------------------------------------- */
/* Stacking cards — sticky title + rows that slide over each other (events)    */
/* -------------------------------------------------------------------------- */

function initStickyStacks() {
    const stacks = document.querySelectorAll('[data-samba-stack]');

    if (!stacks.length || !('ResizeObserver' in window)) return;

    const header = document.querySelector('[data-samba-header].sticky');

    stacks.forEach((stack) => {
        const heading = stack.querySelector('[data-samba-stack-heading]');
        const cards = [...stack.querySelectorAll('[data-samba-stack-card]')];

        if (cards.length < 2) return;

        // Sizes come from the observer entries, never measured on demand: the
        // section sits in a content-visibility wrapper, and reading layout
        // inside a skipped one would force it to render.
        const heights = new Map();

        const update = () => {
            const headingHeight = heading ? heights.get(heading) ?? 0 : 0;
            const tallest = Math.max(0, ...cards.map((card) => heights.get(card) ?? 0));
            const gap = parseFloat(getComputedStyle(stack).getPropertyValue('--samba-stack-gap')) || 0;

            stack.style.setProperty('--samba-stack-heading-h', `${headingHeight}px`);

            // The title must stop pinning exactly when the stack starts to
            // leave — when the last card, pinned gap below the title, runs out
            // of list. A sticky box keeps its bottom margin inside its bounds,
            // so that margin comes off.
            const last = heights.get(cards[cards.length - 1]) ?? 0;
            const margin = heading ? parseFloat(getComputedStyle(heading).marginBottom) || 0 : 0;
            stack.style.setProperty('--samba-stack-tail', `${Math.max(0, gap + last - margin)}px`);

            // Pin only when a whole card fits under the title with the header
            // fully open — otherwise its bottom (the CTA, on mobile) would be
            // covered before anyone saw it.
            const needed = (header ? heights.get(header) ?? 0 : 0) + headingHeight + gap + tallest;
            stack.toggleAttribute('data-stack-on', tallest > 0 && needed <= window.innerHeight);
        };

        const observer = new ResizeObserver((entries) => {
            entries.forEach((entry) => heights.set(entry.target, entry.borderBoxSize[0].blockSize));
            update();
        });

        [header, heading, ...cards].forEach((el) => el && observer.observe(el));
        window.addEventListener('resize', update, { passive: true });
    });
}

/* -------------------------------------------------------------------------- */
/* Search drawer                                                               */
/* -------------------------------------------------------------------------- */

function initSearch() {
    const toggle = document.querySelector('[data-samba-search-toggle]');
    const panel = document.querySelector('[data-samba-search-panel]');

    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';

        toggle.setAttribute('aria-expanded', String(!isOpen));
        panel.hidden = isOpen;

        if (!isOpen) panel.querySelector('input[type="search"]')?.focus();
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || panel.hidden) return;

        toggle.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
        toggle.focus();
    });
}

/* -------------------------------------------------------------------------- */
/* Mobile menu drawer                                                          */
/* -------------------------------------------------------------------------- */

function initMobileMenu() {
    const toggle = document.querySelector('[data-samba-menu-toggle]');
    const panel = document.querySelector('[data-samba-menu-panel]');

    if (!toggle || !panel) return;

    const focusableSelector =
        'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
    const screens = [...panel.querySelectorAll('[data-samba-mscreen]')];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Show one screen; `direction` slides it in from the right (deeper) or left (back).
    const showScreen = (id, direction) => {
        const next = screens.find((screen) => screen.dataset.sambaMscreen === id);
        if (!next) return;

        screens.forEach((screen) => {
            screen.hidden = screen !== next;
        });
        next.scrollTop = 0;

        if (direction && !reduceMotion.matches) {
            next.animate(
                [{ transform: `translateX(${direction === 'back' ? '-24%' : '24%'})`, opacity: 0 }, { transform: 'none', opacity: 1 }],
                { duration: 250, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
            );
        }

        return next;
    };

    panel.querySelectorAll('[data-samba-mscreen-open]').forEach((button) => {
        button.addEventListener('click', () => {
            showScreen(button.dataset.sambaMscreenOpen, 'forward')?.querySelector('[data-samba-mscreen-back]')?.focus();
        });
    });

    panel.querySelectorAll('[data-samba-mscreen-back]').forEach((button) => {
        button.addEventListener('click', () => {
            const from = button.closest('[data-samba-mscreen]').dataset.sambaMscreen;
            const screen = showScreen(button.dataset.sambaMscreenBack, 'back');
            // Back on the button that opened the screen we left.
            screen?.querySelector(`[data-samba-mscreen-open="${from}"]`)?.focus();
        });
    });

    let lastFocused = null;

    const open = () => {
        lastFocused = document.activeElement;
        showScreen('root');
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        document.documentElement.style.overflow = 'hidden';
    };

    const close = (restoreFocus = true) => {
        panel.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        document.documentElement.style.overflow = '';
        if (restoreFocus) (lastFocused instanceof HTMLElement ? lastFocused : toggle).focus();
    };

    toggle.addEventListener('click', () => {
        if (panel.hidden) {
            open();
        } else {
            close();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (panel.hidden) return;

        if (event.key === 'Escape') {
            close();
            return;
        }

        if (event.key !== 'Tab') return;

        // Keep focus in the menu — the × in the header plus the visible screen.
        const focusable = [toggle, ...panel.querySelectorAll(focusableSelector)].filter(
            (el) => el.offsetParent !== null
        );

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    });

    // Close when the viewport grows past the breakpoint the menu serves.
    window.matchMedia('(min-width: 1280px)').addEventListener('change', (event) => {
        if (event.matches && !panel.hidden) close(false);
    });
}

/* -------------------------------------------------------------------------- */
/* Carousels (Embla) — components/carousel.php                                 */
/* -------------------------------------------------------------------------- */

function initCarousels() {
    const carousels = document.querySelectorAll('[data-samba-carousel]');

    if (!carousels.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    carousels.forEach((el) => {
        const viewport = el.querySelector('[data-samba-carousel-viewport]');

        if (!viewport) return;

        // Mobile-only: on desktop the section shows its static grid instead
        // (the carousel itself is lg:hidden), so Embla stands down there.
        const embla = EmblaCarousel(viewport, {
            align: 'start',
            containScroll: 'trimSnaps',
            breakpoints: { '(min-width: 1024px)': { active: false } },
        });

        // Swaps the native scroll-snap fallback for Embla's transform track.
        el.classList.add('is-ready');

        const dotsWrap = el.querySelector('[data-samba-carousel-dots]');
        const dots = dotsWrap ? [...dotsWrap.querySelectorAll('button')] : [];

        if (!dots.length) return;

        dotsWrap.hidden = false;

        dots.forEach((dot, index) => {
            dot.addEventListener('click', () => embla.scrollTo(index, reduceMotion.matches));
        });

        const sync = () => {
            const selected = embla.selectedScrollSnap();

            dots.forEach((dot, index) => {
                if (index === selected) {
                    dot.setAttribute('aria-current', 'true');
                } else {
                    dot.removeAttribute('aria-current');
                }
            });
        };

        embla.on('select', sync).on('reInit', sync);
        sync();
    });
}

/* -------------------------------------------------------------------------- */
/* Quantity steppers                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The − / + buttons on woocommerce/global/quantity-input.php. They are printed
 * hidden and only revealed here, so with this script blocked the number input
 * is on its own and still perfectly usable. WooCommerce's min, max and step
 * are read off the input rather than assumed, and the change event is the
 * native one, which is what the cart page's own script listens for.
 *
 * Delegated, because the cart redraws its rows over AJAX.
 */
function initQuantity() {
    document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-samba-quantity-step]');
        const wrapper = button?.closest('[data-samba-quantity]');
        const input = wrapper?.querySelector('input[type="number"], input.qty');

        if (!button || !input) return;

        const step = Number(input.step) || 1;
        const min = input.min === '' ? 0 : Number(input.min);
        const max = input.max === '' ? Infinity : Number(input.max);
        const current = Number(input.value) || min;

        const next = Math.min(max, Math.max(min, current + step * Number(button.dataset.sambaQuantityStep)));

        if (next === current) return;

        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    const reveal = (root) => {
        root.querySelectorAll('[data-samba-quantity] [data-samba-quantity-step]').forEach((button) => {
            button.hidden = false;
        });
    };

    reveal(document);

    // The cart replaces its markup wholesale after an update.
    new MutationObserver((records) => {
        records.forEach((record) => {
            record.addedNodes.forEach((node) => {
                if (node.nodeType === 1) reveal(node);
            });
        });
    }).observe(document.body, { childList: true, subtree: true });
}

/* -------------------------------------------------------------------------- */
/* Homepage section reveal — skeleton → content as each section nears view    */
/* -------------------------------------------------------------------------- */

function initSectionReveal() {
    const root = document.documentElement;

    // Set in <head> only when skeletons are on (whytheme_print_reveal_bootstrap());
    // if its 5s failsafe already fired, everything is visible — leave it.
    if (!root.classList.contains('samba-reveal')) return;

    window.sambaRevealReady = true;

    const sections = document.querySelectorAll('[data-samba-reveal]');

    if (!sections.length) return;

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                entry.target.classList.add('is-revealed');
                observer.unobserve(entry.target);
            });
        },
        { rootMargin: '200px 0px' }
    );

    sections.forEach((el) => observer.observe(el));
}

/* -------------------------------------------------------------------------- */
/* Wishlist hearts (product cards) — inc/woocommerce/wishlist.php              */
/* -------------------------------------------------------------------------- */

function initWishlist() {
    const config = window.sambaWishlist;

    if (!config) return;

    // Every copy of a product's heart (the homepage renders cards twice:
    // mobile carousel + desktop grid) reflects the same state.
    const setState = (id, saved) => {
        document.querySelectorAll(`[data-samba-wishlist="${id}"]`).forEach((btn) => {
            btn.setAttribute('aria-pressed', String(saved));
        });
    };

    document.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-samba-wishlist]');

        if (!button) return;

        event.preventDefault();

        if (!config.loggedIn) {
            const url = new URL(config.loginUrl, window.location.origin);
            url.searchParams.set('redirect_to', window.location.href);
            window.location.assign(url.toString());
            return;
        }

        const id = button.dataset.sambaWishlist;
        const wasSaved = button.getAttribute('aria-pressed') === 'true';

        setState(id, !wasSaved); // optimistic

        try {
            const body = new FormData();
            body.append('action', 'samba_wishlist_toggle');
            body.append('nonce', config.nonce);
            body.append('product_id', id);

            const response = await fetch(config.ajaxUrl, { method: 'POST', body, credentials: 'same-origin' });
            const json = await response.json();

            if (!json.success) throw new Error(json.data?.code || 'wishlist_failed');

            setState(id, json.data.saved);
        } catch (error) {
            setState(id, wasSaved);
        }
    });
}

/* -------------------------------------------------------------------------- */
/* Accordions — animated <details> (components/accordion.php)                 */
/* -------------------------------------------------------------------------- */

/**
 * Native <details> opens and closes instantly, and its content can't be
 * transitioned to height:auto — so measure the real heights in JS and animate
 * the <details> box between them (Web Animations API). `open` is set before
 * expanding (so the content exists to measure) and removed only after
 * collapsing (so it stays visible on the way down); data-closing flips the
 * chevron straight away.
 *
 * Exclusivity moves from the `name` attribute to here: with `name`, opening
 * one panel makes the browser snap its sibling shut mid-animation. Without
 * this script the markup keeps `name`, so the native behaviour still applies.
 */
function initAccordions() {
    const panels = document.querySelectorAll('details[data-samba-accordion]');

    if (!panels.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const running = new WeakMap();
    const options = { duration: 300, easing: 'cubic-bezier(0.2, 0, 0, 1)' };

    // Height of the box with only the summary showing (border-box).
    const closedHeight = (el) => {
        const style = getComputedStyle(el);
        return el.querySelector('summary').offsetHeight + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    };

    const animate = (el, from, to, done) => {
        running.get(el)?.cancel();

        if (reduceMotion.matches || from === to) {
            done?.();
            return;
        }

        el.style.overflow = 'hidden';
        const animation = el.animate({ height: [`${from}px`, `${to}px`] }, options);
        running.set(el, animation);

        // `finished` rejects on cancel (a re-click mid-animation), so the
        // superseded animation's `done` never runs.
        animation.finished
            .then(() => {
                running.delete(el);
                el.style.overflow = '';
                done?.();
            })
            .catch(() => {
                // A replacement animation may already be running — leave its overflow alone.
                if (running.get(el) === animation) running.delete(el);
                if (!running.has(el)) el.style.overflow = '';
            });
    };

    const collapse = (el) => {
        const from = el.offsetHeight; // mid-animation height if one is running
        el.dataset.closing = '';
        animate(el, from, closedHeight(el), () => {
            el.open = false;
            delete el.dataset.closing;
        });
    };

    const expand = (el) => {
        const from = el.offsetHeight;
        running.get(el)?.cancel();
        delete el.dataset.closing;
        el.open = true;
        animate(el, from, el.offsetHeight);

        const group = el.dataset.sambaGroup;
        if (!group) return;

        panels.forEach((other) => {
            if (other !== el && other.dataset.sambaGroup === group && other.open && !('closing' in other.dataset)) {
                collapse(other);
            }
        });
    };

    panels.forEach((el) => {
        const name = el.getAttribute('name');
        if (name) {
            el.dataset.sambaGroup = name;
            el.removeAttribute('name');
        }

        // Click covers Enter/Space on the summary too.
        el.querySelector('summary')?.addEventListener('click', (event) => {
            event.preventDefault();

            if (el.open && !('closing' in el.dataset)) {
                collapse(el);
            } else {
                expand(el);
            }
        });
    });
}

/* -------------------------------------------------------------------------- */

function init() {
    initSectionReveal();
    initAnimations();
    initWishlist();
    initDropdowns();
    initMegaMenus();
    initHeaderCollapse();
    initStickyStacks();
    initSearch();
    initMobileMenu();
    initCarousels();
    initQuantity();
    initAccordions();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
